import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";

interface KeyValue {
  key: string;
  value: string;
}

export enum WebhookEvent {
  REQUEST_CREATE = "REQUEST_CREATE",
  REQUEST_UPDATE = "REQUEST_UPDATE",
  ROOM_UPDATE = "ROOM_UPDATE",
}

export enum WebhookType {
  KAFKA = "KAFKA",
  HTTPS = "HTTPS",
}

export interface CustomerWebhook {
  id: number;
  customerId: number;
  url: string;
  name: string;
  event: WebhookEvent;
  type: WebhookType;
  headers: KeyValue[];
  headersJson: string;
  createdBy: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  includeData: boolean;
  includeDemoStores: boolean;
}

export interface CustomerWebhookCreateRequest {
  customerId: number;
  url: string;
  name: string;
  event: WebhookEvent;
  type: WebhookType;
  headers: KeyValue[];
  includeData: boolean;
  includeDemoStores: boolean;
}

export interface CustomerWebhookUpdateRequest {
  id: number;
  customerId: number;
  url: string;
  name: string;
  event: WebhookEvent;
  type: WebhookType;
  headers: KeyValue[];
  enabled: boolean;
  includeData: boolean;
  includeDemoStores: boolean;
}

export interface WebhooksResponse {
  data: CustomerWebhook[];
  success: boolean;
  message?: string;
}

export interface WebhookResponse {
  data: CustomerWebhook;
  success: boolean;
  message?: string;
}

export interface WebhookOperationResponse {
  success: boolean;
  message?: string;
}

export class WebhookService extends BaseApiService {
  private static instance: WebhookService;

  public static getInstance(): WebhookService {
    if (!WebhookService.instance) {
      WebhookService.instance = new WebhookService();
    }
    return WebhookService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  /**
   * Get webhooks by customer ID
   */
  async getWebhooksByCustomerId(customerId: number): Promise<WebhooksResponse> {
    try {
      const response = await this.get<{ data: CustomerWebhook[] }>(
        `/webhook/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );
      console.log("webkeys", response.data || []);
      return {
        data: response.data || [],
        success: true,
      };
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      return {
        data: [],
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch webhooks",
      };
    }
  }

  /**
   * Create a new webhook
   */
  async createWebhook(request: CustomerWebhookCreateRequest): Promise<WebhookOperationResponse> {
    try {
      await this.post<CustomerWebhook>("/webhook", request, this.getCoreBaseUrl());
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error creating webhook:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create webhook",
      };
    }
  }

  /**
   * Update an existing webhook
   */
  async updateWebhook(request: CustomerWebhookUpdateRequest): Promise<WebhookOperationResponse> {
    try {
      await this.put<CustomerWebhook>("/webhook", request, this.getCoreBaseUrl());
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error updating webhook:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update webhook",
      };
    }
  }

  /**
   * Enable or disable a webhook
   */
  async setWebhookEnabled(id: number, enabled: boolean): Promise<WebhookOperationResponse> {
    try {
      await this.patch<CustomerWebhook>(
        `/webhook/${id}/enabled/${enabled}`,
        null,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error updating webhook status:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update webhook status",
      };
    }
  }

  /**
   * Get webhook by ID
   */
  async getWebhookById(id: number): Promise<WebhookResponse> {
    try {
      const response = await this.get<{ data: CustomerWebhook }>(
        `/webhook/${id}`,
        this.getCoreBaseUrl(),
      );
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      console.error("Error fetching webhook:", error);
      return {
        data: {} as CustomerWebhook,
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch webhook",
      };
    }
  }

  /**
   * Get all webhooks
   */
  async getAllWebhooks(): Promise<WebhooksResponse> {
    try {
      const response = await this.get<{ data: CustomerWebhook[] }>(
        "/webhook",
        this.getCoreBaseUrl(),
      );
      return {
        data: response.data || [],
        success: true,
      };
    } catch (error) {
      console.error("Error fetching all webhooks:", error);
      return {
        data: [],
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch all webhooks",
      };
    }
  }
}
